import {ArchiveRecord} from 'archive/ArchiveConstants';
import {updateItemsOnArchive, updateItemsOnUnarchive} from 'archive/ArchiveStoreUtil';
import {BenchlingAction} from 'redux-stores/Action';
import {RequestSchema} from 'requests/RequestConstants';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {RequestSchemaActionType} from './RequestSchemaReduxActions';
import {RequestTaskSchemaActionType} from './RequestTaskSchemaReduxActions';
import sortRequestTaskSchemas from './sortRequestTaskSchemas';

export interface RequestSchemasState {
  requestTaskSchemasById: {[schemaId: string]: RequestTaskSchema} | null;
  requestSchemasById: {[schemaId: string]: RequestSchema} | null;
}

export const REQUEST_SCHEMAS_INITIAL_STATE: RequestSchemasState = {
  requestTaskSchemasById: null,
  requestSchemasById: null,
};

function _updateRequestTaskSchemasFromArchiveAction(
  state: RequestSchemasState,
  schemaIds: Array<string>,
  archiveRecordsBySchemaId: {[schemaId: string]: ArchiveRecord}
): RequestSchemasState {
  return state.requestTaskSchemasById == null
    ? state
    : {
        ...state,
        requestTaskSchemasById: updateItemsOnArchive(
          state.requestTaskSchemasById,
          schemaIds,
          archiveRecordsBySchemaId
        ),
      };
}

function _updateRequestTaskSchemasFromUnarchiveAction(
  state: RequestSchemasState,
  schemaIds: Array<string>
): RequestSchemasState {
  return state.requestTaskSchemasById == null
    ? state
    : {
        ...state,
        requestTaskSchemasById: updateItemsOnUnarchive(state.requestTaskSchemasById, schemaIds),
      };
}

function _updateRequestSchemasFromArchiveAction(
  state: RequestSchemasState,
  schemaIds: Array<string>,
  archiveRecordsBySchemaId: {[schemaId: string]: ArchiveRecord}
): RequestSchemasState {
  return state.requestSchemasById == null
    ? state
    : {
        ...state,
        requestSchemasById: updateItemsOnArchive(
          state.requestSchemasById,
          _getChangedRequestSchemaIds(state, schemaIds),
          archiveRecordsBySchemaId
        ),
      };
}

function _updateRequestSchemasFromUnarchiveAction(
  state: RequestSchemasState,
  schemaIds: Array<string>
): RequestSchemasState {
  return state.requestSchemasById == null
    ? state
    : {
        ...state,
        requestSchemasById: updateItemsOnUnarchive(
          state.requestSchemasById,
          _getChangedRequestSchemaIds(state, schemaIds)
        ),
      };
}

/**
 * @param changedIds: can contain any assay schema api ids; will be filtered to only those in
 * `requestSchemasById`. This behavior was written specifically to handle the use case of a list of
 * undifferentiated assay schema ids, since it is not trivial to figure out an assay schema's type from
 * only the id without crossing store boundaries.
 */
function _getChangedRequestSchemaIds(state: RequestSchemasState, changedIds: Array<string>): Array<string> {
  return state.requestSchemasById == null
    ? []
    : changedIds.filter((id) => state.requestSchemasById!.hasOwnProperty(id));
}

/**
 * Note: Request schemas in the redux store should be synced with the flux AssayStore in all actions.
 */
export default function reduceRequestSchemas(
  state: RequestSchemasState = REQUEST_SCHEMAS_INITIAL_STATE,
  action: BenchlingAction
): RequestSchemasState {
  switch (action.type) {
    case RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_FETCHED: {
      const {requestTaskSchemasById} = action;
      return {
        ...state,
        requestTaskSchemasById,
      };
    }
    case RequestTaskSchemaActionType.REQUEST_TASK_SCHEMA_FETCHED: {
      const {requestTaskSchema} = action;
      const requestTaskSchemasById = {
        ...state.requestTaskSchemasById,
        [requestTaskSchema.id]: requestTaskSchema,
      };
      return {
        ...state,
        requestTaskSchemasById,
      };
    }
    case RequestSchemaActionType.REQUEST_SCHEMAS_LISTED: {
      const {requestSchemasById} = action;
      return {
        ...state,
        requestSchemasById,
      };
    }
    case RequestSchemaActionType.REQUEST_SCHEMA_FETCHED: {
      const {requestSchema} = action;
      const requestSchemasById = {
        ...state.requestSchemasById,
        [requestSchema.id]: requestSchema,
      };
      return {
        ...state,
        requestSchemasById,
      };
    }
    case RequestSchemaActionType.REQUEST_SCHEMAS_UPDATED_FROM_GQL: {
      const {requestSchemasById} = action;
      const newRequestSchemasById = {
        ...state.requestSchemasById,
        ...requestSchemasById,
      };
      return {
        ...state,
        requestSchemasById: newRequestSchemasById,
      };
    }
    case RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_ARCHIVED: {
      const {itemIds: schemaIds, archiveRecordsByItemId: archiveRecordsBySchemaId} = action;
      return _updateRequestTaskSchemasFromArchiveAction(state, schemaIds, archiveRecordsBySchemaId);
    }
    case RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UNARCHIVED: {
      const {itemIds: schemaIds} = action;
      return _updateRequestTaskSchemasFromUnarchiveAction(state, schemaIds);
    }
    case RequestTaskSchemaActionType.REQUEST_TASK_SCHEMAS_UPDATED_FROM_GQL: {
      const {requestTaskSchemasById} = action;
      return {...state, requestTaskSchemasById: {...state.requestTaskSchemasById, ...requestTaskSchemasById}};
    }
    case RequestSchemaActionType.REQUEST_SCHEMAS_ARCHIVED: {
      const {itemIds: schemaIds, archiveRecordsByItemId: archiveRecordsBySchemaId} = action;
      return _updateRequestSchemasFromArchiveAction(state, schemaIds, archiveRecordsBySchemaId);
    }
    case RequestSchemaActionType.REQUEST_SCHEMAS_UNARCHIVED: {
      const {itemIds: schemaIds} = action;
      return _updateRequestSchemasFromUnarchiveAction(state, schemaIds);
    }
    default:
      return state;
  }
}

export function getSortedRequestTaskSchemas(
  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL> | null
): Array<RequestTaskSchemaGraphQL> | null {
  if (requestTaskSchemasById == null) {
    return null;
  }
  const requestTaskSchemas = Object.values(requestTaskSchemasById);
  return sortRequestTaskSchemas(requestTaskSchemas);
}
